import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useOrdersList } from '../api/hooks';
import { OrdersTable } from '../components/OrdersTable';
import { hasRight, useAuthStore } from '../auth/store';
import type { OrderStatusSchema } from '@wave/shared';
import { btnPrimary, filterActive, filterIdle, pageContainer, pageHeader, pageTitle } from '../lib/styles';

const FILTERS: { value: 'all' | OrderStatusSchema; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'new', label: 'Новые' },
  { value: 'progress', label: 'В работе' },
  { value: 'closed', label: 'Закрытые' },
  { value: 'reject', label: 'Реджект' },
];

export function OrdersPage() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? 'all') as 'all' | OrderStatusSchema;

  // Поиск: локальный ввод + debounce (не дёргаем сервер на каждый символ).
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, error } = useOrdersList(status, search);
  const navigate = useNavigate();
  const location = useLocation();
  const rights = useAuthStore((s) => s.rights);
  const canCreate = hasRight(rights, 'orders.create');
  const canDelete = hasRight(rights, 'orders.delete');
  const filters = canDelete
    ? [...FILTERS, { value: 'deleted' as const, label: 'Удалённые' }]
    : FILTERS;

  return (
    <div className={pageContainer}>
      <div className={pageHeader}>
        <h1 className={pageTitle}>Заявки</h1>
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setParams(f.value === 'all' ? {} : { status: f.value })}
              className={status === f.value ? filterActive : filterIdle}
            >
              {f.label}
            </button>
          ))}
          {canCreate && (
            <button
              onClick={() => navigate('/orders/new', { state: { backgroundLocation: location } })}
              className={`ml-2 ${btnPrimary}`}
            >
              + Новая заявка
            </button>
          )}
        </div>
      </div>

      {/* Поиск по номеру заявки, клиенту, исполнителю, чату */}
      <div className="mb-4 max-w-md">
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-subtle">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Поиск: № заявки, клиент, исполнитель, чат…"
            className="block h-9 w-full rounded-md border border-border bg-bg pl-8 pr-8 text-sm text-text outline-none transition-colors duration-150 hover:border-border-strong focus:border-accent focus:shadow-focus placeholder:text-text-subtle"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Очистить поиск"
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded text-text-subtle transition-colors duration-150 hover:bg-bg3 hover:text-text"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {isLoading && <div className="text-sm text-text2">Загрузка…</div>}
      {error && (
        <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}
      {data && (
        <>
          <div className="mb-3 text-xs font-medium text-text2">
            {search ? (
              <>
                Найдено по «<span className="text-text">{search}</span>»:{' '}
                <span className="text-text">{data.total}</span>
              </>
            ) : (
              <>
                Найдено: <span className="text-text">{data.total}</span>
              </>
            )}
          </div>
          {data.items.length === 0 ? (
            <div className="rounded-lg border border-border bg-bg2 px-6 py-12 text-center">
              <div className="text-sm font-medium text-text">Ничего не найдено</div>
              <div className="mt-1 text-xs text-text2">
                {search ? 'Измените поисковый запрос или фильтр статуса.' : 'Заявок в этом статусе пока нет.'}
              </div>
            </div>
          ) : (
            <OrdersTable orders={data.items} />
          )}
        </>
      )}
    </div>
  );
}
